import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './AuthContext'
import { RENAMED_MODULE_KEYS } from '../config/moduleConfig'

const TenantContext = createContext(null)

/**
 * Apply a saved order to the enabled modules list.
 * Modules in savedOrder come first (in saved order), then any remaining
 * modules are placed using fallbackOrder (if provided), then any still
 * remaining are appended at the end.
 */
function applyOrder(enabledModules, savedOrder, fallbackOrder) {
  if (!savedOrder || !Array.isArray(savedOrder) || savedOrder.length === 0) {
    return enabledModules
  }

  const moduleMap = new Map(enabledModules.map(m => [m.module_name, m]))
  const ordered = []

  // Add modules in saved order (skip any that are no longer enabled)
  for (const key of savedOrder) {
    if (moduleMap.has(key)) {
      ordered.push(moduleMap.get(key))
      moduleMap.delete(key)
    }
  }

  // Place remaining modules using fallback order (e.g. admin order for new modules)
  if (fallbackOrder && Array.isArray(fallbackOrder) && moduleMap.size > 0) {
    for (const key of fallbackOrder) {
      if (moduleMap.has(key)) {
        ordered.push(moduleMap.get(key))
        moduleMap.delete(key)
      }
    }
  }

  // Append any still remaining modules
  for (const m of moduleMap.values()) {
    ordered.push(m)
  }

  return ordered
}

export function TenantProvider({ children }) {
  const { isAuthenticated, getAccessToken } = useAuth()
  const [tenantState, setTenantState] = useState({
    tenant: null,
    modules: [],
    orderedModules: [],
    adminModuleOrder: null,
    userModuleOrder: null,
    isLoading: true,
    error: null
  })

  const loadData = useCallback(async () => {
    if (!isAuthenticated) {
      setTenantState(prev => ({ ...prev, isLoading: false }))
      return
    }

    try {
      const token = getAccessToken()
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {}

      // Include platform headers for tenant override (read from localStorage)
      const platformTenantId = localStorage.getItem('platformTenantId')
      const platformHeaders = {}
      if (platformTenantId) {
        platformHeaders['X-Tenant-Id'] = platformTenantId
      }

      // Fetch tenant info, admin module order, and user preferences in parallel
      const [tenantRes, adminOrderRes, userPrefsRes] = await Promise.all([
        fetch('/api/tenant/current', { headers: { ...authHeaders, ...platformHeaders } }),
        fetch('/api/settings/module_order', { headers: authHeaders }),
        fetch('/api/user-preferences', { headers: authHeaders })
      ])

      if (!tenantRes.ok) {
        throw new Error('Failed to load tenant information')
      }

      const tenantData = await tenantRes.json()
      const adminOrderData = await adminOrderRes.json()
      const userPrefsData = await userPrefsRes.json()

      // Old-key copies are for app builds from before a rename (server
      // core/legacyNames.js); this build knows the new keys.
      const modules = (tenantData.modules || []).filter(m => !m.legacy_alias)
      const enabledModules = modules.filter(m => m.is_enabled)

      // A saved order may still hold a module's old key -- written before the
      // rename, or since by an old app build.
      const currentKeys = (order) => Array.isArray(order)
        ? [...new Set(order.map(k => RENAMED_MODULE_KEYS[k] || k))]
        : order
      const adminOrder = currentKeys(adminOrderData.success && adminOrderData.setting
        ? adminOrderData.setting.value
        : null)
      const userOrder = currentKeys(userPrefsData.success && userPrefsData.preferences
        ? userPrefsData.preferences.moduleOrder
        : null)

      // Cascade: user override → admin default → database order
      // When user has a saved order, use admin order as fallback for new modules
      let orderedModules
      if (userOrder && Array.isArray(userOrder) && userOrder.length > 0) {
        orderedModules = applyOrder(enabledModules, userOrder, adminOrder)
      } else if (adminOrder && Array.isArray(adminOrder) && adminOrder.length > 0) {
        orderedModules = applyOrder(enabledModules, adminOrder)
      } else {
        orderedModules = enabledModules
      }

      setTenantState({
        tenant: tenantData.tenant,
        modules,
        orderedModules,
        adminModuleOrder: adminOrder,
        userModuleOrder: userOrder,
        isLoading: false,
        error: null
      })
    } catch (error) {
      console.error('Tenant load error:', error)
      setTenantState({
        tenant: null,
        modules: [],
        orderedModules: [],
        adminModuleOrder: null,
        userModuleOrder: null,
        isLoading: false,
        error: error.message
      })
    }
  }, [isAuthenticated, getAccessToken])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Check if a specific module is enabled
  const isModuleEnabled = (moduleName) => {
    return tenantState.modules.some(m => m.module_name === moduleName && m.is_enabled)
  }

  // Get module config
  const getModuleConfig = (moduleName) => {
    const module = tenantState.modules.find(m => m.module_name === moduleName)
    return module?.config || {}
  }

  const value = {
    ...tenantState,
    isModuleEnabled,
    getModuleConfig,
    refreshTenant: loadData
  }

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant() {
  const context = useContext(TenantContext)
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}

export default TenantContext
